import type { LocationQuery, RouteLocationRaw } from 'vue-router'
import { buildScopedListQuery } from '@/shared/utils/scopedContext'
import type { TabItem } from './types'
import GoogleIcon from '@/shared/components/icons/GoogleIcon.vue'
import MicrosoftIcon from '@/shared/components/icons/MicrosoftIcon.vue'
import HighLevelIcon from '@/shared/components/icons/HighLevelIcon.vue'

export const UC_USER_MESSAGING_LIST_ROUTE_NAME = 'uc-user-messaging' as const
export const UC_USER_MESSAGING_SHOW_ROUTE_NAME = 'uc-user-messaging-show' as const

export const UC_USER_MESSAGING_ENDPOINT = '/messaging' as const
export const UC_USER_MESSAGING_ACCOUNT_QUERY_KEY = 'account_id' as const
export const MESSAGING_THREAD_QUERY_KEY = 'message_thread_id' as const
export const MESSAGING_DEFAULT_GROUP_RECIPIENT_LIMIT = 7
export const MESSAGING_THREADS_PER_PAGE = 100
export const MESSAGING_MESSAGES_PER_PAGE = 100
export const MESSAGING_MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
export const MESSAGING_MAX_SEGMENTS = 10

export const CREATE_CONVERSATION_METHOD = {
  enter: 'enter',
  phonebook: 'phonebook',
  google: 'google',
  microsoft: 'microsoft',
  highlevel: 'highlevel'
} as const

export const CREATE_CONVERSATION_METHOD_OPTIONS = [
  { label: 'Enter', value: CREATE_CONVERSATION_METHOD.enter },
  { label: 'Phonebook', value: CREATE_CONVERSATION_METHOD.phonebook },
  { label: 'Google', value: CREATE_CONVERSATION_METHOD.google, icon: GoogleIcon },
  { label: 'Microsoft', value: CREATE_CONVERSATION_METHOD.microsoft, icon: MicrosoftIcon },
  { label: 'HighLevel', value: CREATE_CONVERSATION_METHOD.highlevel, icon: HighLevelIcon }
] as const

export const UC_USER_MESSAGING_TEST_IDS = {
  page: 'uc-user-messaging-page',
  showPage: 'uc-user-messaging-show-page',
  accountTable: 'uc-user-messaging-account-table',
  phoneTable: 'uc-user-messaging-phone-table',
  switcher: 'uc-user-messaging-switcher',
  tabMessages: 'uc-user-messaging-tab-messages',
  tabTemplates: 'uc-user-messaging-tab-templates',
  tabSettings: 'uc-user-messaging-tab-settings',
  tabOptOuts: 'uc-user-messaging-tab-opt-outs',
  templateAddBtn: 'uc-user-messaging-template-add-button',
  optOutAddBtn: 'uc-user-messaging-opt-out-add-button',
  settingsSubmitBtn: 'uc-user-messaging-settings-submit-button',
  messagesTab: 'uc-user-messaging-messages-tab',
  threadsList: 'uc-user-messaging-threads-list',
  threadsSearch: 'uc-user-messaging-threads-search',
  threadsTrashToggle: 'uc-user-messaging-threads-trash-toggle',
  createThreadButton: 'uc-user-messaging-create-thread-button',
  createThreadDialog: 'uc-user-messaging-create-thread-dialog',
  conversation: 'uc-user-messaging-conversation',
  composeInput: 'uc-user-messaging-compose-input',
  composeSubmit: 'uc-user-messaging-compose-submit',
  composeSegments: 'uc-user-messaging-compose-segments',
  composeAttachments: 'uc-user-messaging-compose-attachments',
  tabBulk: 'uc-user-messaging-tab-bulk',
  bulkTab: 'uc-user-messaging-bulk-tab',
  bulkCsvInput: 'uc-user-messaging-bulk-csv-input',
  bulkSubmit: 'uc-user-messaging-bulk-submit',
  bulkExampleDownload: 'uc-user-messaging-bulk-example-download',
  createBulkMenuItem: 'uc-user-messaging-create-bulk-menu-item'
} as const

export const BULK_CSV_HEADERS = ['Phone Number', '2-Digit Country Code', 'Custom Message'] as const

export const BULK_CSV_EXAMPLE_ROWS: string[][] = [
  ['1-839-755-2321', 'US', ''],
  ['(527) 758-2081', 'US', 'Hey Rob, this is...'],
  ['2743106588', 'US', '']
]

export function buildUcUserMessagingTabs(canBulkSend = false): TabItem[] {
  const tabs: TabItem[] = [
    { id: 'messages', label: 'Messages', testId: UC_USER_MESSAGING_TEST_IDS.tabMessages }
  ]
  if (canBulkSend) {
    tabs.push({
      id: 'send_bulk',
      label: 'Send Bulk Message',
      testId: UC_USER_MESSAGING_TEST_IDS.tabBulk
    })
  }
  tabs.push(
    { id: 'templates', label: 'Templates', testId: UC_USER_MESSAGING_TEST_IDS.tabTemplates },
    { id: 'settings', label: 'Settings', testId: UC_USER_MESSAGING_TEST_IDS.tabSettings },
    { id: 'sms-opt-outs', label: 'SMS Opt-Outs', testId: UC_USER_MESSAGING_TEST_IDS.tabOptOuts }
  )
  return tabs
}

export const ucUserMessagingTabs: TabItem[] = buildUcUserMessagingTabs()

export function buildUcUserMessagingListQuery(
  accountId?: string | number | null,
  routeQuery?: LocationQuery
) {
  return buildScopedListQuery({ accountId }, routeQuery)
}

export function buildUcUserMessagingListRoute(
  accountId?: string | number | null,
  routeQuery?: LocationQuery,
  resellerId?: string | number | null
): RouteLocationRaw {
  const query = {
    ...buildUcUserMessagingListQuery(accountId, routeQuery),
    ...(resellerId ? { reseller_id: String(resellerId) } : {})
  }
  delete (query as Record<string, unknown>).tab
  delete (query as Record<string, unknown>)[MESSAGING_THREAD_QUERY_KEY]
  return {
    name: UC_USER_MESSAGING_LIST_ROUTE_NAME,
    query
  }
}

export function buildUcUserMessagingShowRoute(
  phoneNumberId: string | number,
  accountId?: string | number | null,
  routeQuery?: LocationQuery,
  tab?: string | null,
  messageThreadId?: string | null
): RouteLocationRaw {
  const query = { ...buildUcUserMessagingListQuery(accountId, routeQuery) }
  const nextTab = tab || (typeof query.tab === 'string' ? query.tab : 'messages')
  query.tab = nextTab
  if (messageThreadId) {
    query[MESSAGING_THREAD_QUERY_KEY] = String(messageThreadId)
  } else {
    delete query[MESSAGING_THREAD_QUERY_KEY]
  }
  return {
    name: UC_USER_MESSAGING_SHOW_ROUTE_NAME,
    params: { phoneNumberId: String(phoneNumberId) },
    query
  }
}
